<template>
  <section ref="supervisorInformationCenter" class="supervisor-information-center">
    <v-overlay :value="isLoading" absolute z-index="109">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <EcsDynamicGrid
      ref="businessProcessesGrid"
      class="grid"
      :cols="cols"
      :gap="16"
      :grid-drag-start-predicate="gridDragStartPredicate"
      :on-drag-end-card="onDragEndCard"
      :drag-enabled="true"
      :sort-data="sortData"
    >
      <EcsDynamicGridItem
        v-for="businessProcess in supervisedBusinessProcesses"
        :id="businessProcess.businessProcessId"
        :key="businessProcess.businessProcessId"
        class="item"
        :container-background-style="gridItemStyles[businessProcess.businessProcessId]"
        :show-divider="false"
      >
        <template #default="{ expanded }">
          <EcsBusinessProcessMonitoringCard
            :ref="businessProcess.businessProcessId"
            class="monitoring-card"
            :expanded="expanded"
            :business-process-id="businessProcess.businessProcessId"
            :width="monitoringCardWidth"
            @critical-status-changed="onHealthUpdate"
          />
        </template>
      </EcsDynamicGridItem>
    </EcsDynamicGrid>

    <v-btn class="dashboard-setup-btn" color="accent" fab @click.stop="onSetupClick">
      <v-icon>mdi-view-dashboard</v-icon>
    </v-btn>

    <EcsSupervisorDashboardOptionsDialog ref="setupDialog" @update-dashboard="handleUpdateDashboard" />
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';
import Muuri from 'muuri';
import { STATS_SORTING } from '@/constants/businessProcess/constants';
import EcsBusinessProcessMonitoringCard from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessMonitoringCard';
import EcsSupervisorDashboardOptionsDialog from '@/components/ecs-supervisor-information-center/EcsSupervisorDashboardOptionsDialog';
import EcsDynamicGrid from '@/components/ecs-dynamic-grid/EcsDynamicGrid';
import EcsDynamicGridItem from '@/components/ecs-dynamic-grid/EcsDynamicGridItem';

export default {
  name: 'EcsSupervisorInformationCenter',

  components: {
    EcsBusinessProcessMonitoringCard,
    EcsSupervisorDashboardOptionsDialog,
    EcsDynamicGrid,
    EcsDynamicGridItem
  },

  data() {
    return {
      grid: null,
      isGridLoading: false,
      monitoringCardWidth: 500,
      resizeObserver: null,
      currentCardsOrder: [],
      options: {
        sorting: STATS_SORTING.Health
      },
      cols: 2,
      gridItemStyles: {}
    };
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', ['supervisedBusinessProcesses', 'businessProcessHealthConfigurationTemplates']),

    ...mapGetters('SETTINGS', ['supervisorDashboardSettings']),

    isLoading() {
      return !this.supervisedBusinessProcesses.length || !this.businessProcessHealthConfigurationTemplates.length;
    },

    sortData() {
      if (this.$refs === undefined) {
        return {};
      }
      return {
        status: (item, element) => {
          if (this.$refs !== undefined) {
            const card = this.$refs[element.id];
            return card && card[0] && card[0].criticalKpi ? card[0].criticalKpi.status : -1;
          }
          return -1;
        },
        name: (item, element) => {
          const businessProcess = this.supervisedBusinessProcesses.find(bp => bp.businessProcessId === element.id);
          return businessProcess ? businessProcess.businessProcessName.toLowerCase() : '';
        },
        order: (item, element) => {
          return this.currentCardsOrder.findIndex(id => id === element.id);
        }
      };
    }
  },

  watch: {
    async supervisedBusinessProcesses() {
      await this.$nextTick();
      this.initializeMuuri();
    },

    'options.sorting'(value) {
      this.sortMonitoringCards();
      this.updateSupervisorDashboardSorting();
    }
  },

  created() {
    this.requestBusinessProcessHealthConfigurationTemplates();
  },

  mounted() {
    if (this.supervisorDashboardSettings) {
      this.options.sorting = this.supervisorDashboardSettings.adaptiveDashboards.sorting;
    }

    this.initializeMuuri();
    this.initializeCards();
    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.$refs.supervisorInformationCenter);
  },

  beforeDestroy() {
    this.resizeObserver.unobserve(this.$refs.supervisorInformationCenter);
    delete this.resizeObserver;
  },

  methods: {
    ...mapActions('BUSINESS_PROCESSES', ['requestBusinessProcessHealthConfigurationTemplates']),

    ...mapActions('SETTINGS', ['updateSupervisorAdaptiveDashboardSettings']),

    initializeMuuri() {
      this.currentCardsOrder = this.supervisorDashboardSettings.adaptiveDashboards.cards
        .sort((a, b) => a.order - b.order)
        .map(card => card.businessProcessId);
      this.$refs.businessProcessesGrid.initializeGrid();
      this.sortMonitoringCards();
    },

    initializeCards() {
      if (!this.$refs.supervisorInformationCenter) {
        return;
      }

      const spaceBetweenCards = 16;
      const containerPaddings = 12;
      const scrollbar = 6;
      const containerWidth =
        this.$refs.supervisorInformationCenter.getBoundingClientRect().width - containerPaddings - scrollbar;
      const maxCards = Math.trunc(containerWidth / (500 + spaceBetweenCards));
      this.monitoringCardWidth = ((containerWidth / maxCards) | 0) - spaceBetweenCards;
      this.cols = maxCards;
    },

    onResize: debounce(function() {
      this.initializeCards();
      const transitionTime = 200;

      setTimeout(() => {
        this.$refs.businessProcessesGrid.refreshItems();
        this.$refs.businessProcessesGrid.layout();
      }, transitionTime);
    }, 100),

    getMuuriItemsOrder() {
      return this.$refs.businessProcessesGrid.getMuuriItemsOrder() || [];
    },

    onDragEndCard() {
      const orderedCardsIds = this.getMuuriItemsOrder();
      if (this.currentCardsOrder.join() === orderedCardsIds.join()) {
        return;
      }

      this.updateSupervisorDashboardCardsOrder();
    },

    updateSupervisorDashboardSorting() {
      const settings = this.options.sorting;
      this.updateSupervisorAdaptiveDashboardSettings({
        property: 'supervisorDashboardSettings.adaptiveDashboards.sorting',
        settings
      });
    },

    updateSupervisorDashboardCardsOrder() {
      this.currentCardsOrder = this.getMuuriItemsOrder();

      const cards = [...this.supervisorDashboardSettings.adaptiveDashboards.cards];
      this.supervisedBusinessProcesses.forEach(({ businessProcessId }) => {
        const cardSettingsIndex = cards.findIndex(card => card.businessProcessId === businessProcessId);
        const order = this.currentCardsOrder.findIndex(id => businessProcessId === id);

        if (order !== -1) {
          if (cardSettingsIndex === -1) {
            cards.push({
              businessProcessId,
              selectedCardIndex: 3,
              isPinned: false,
              order
            });
          } else {
            cards.splice(cardSettingsIndex, 1, { ...cards[cardSettingsIndex], order });
          }
        }
      });
      this.updateSupervisorAdaptiveDashboardSettings({
        property: 'supervisorDashboardSettings.adaptiveDashboards.cards',
        settings: cards
      });
    },

    onHealthUpdate(id, status) {
      if (this.options.sorting === STATS_SORTING.Health) {
        this.sortMonitoringCards();
      }
      this.$set(this.gridItemStyles, id, status);
    },

    sortMonitoringCards() {
      if (this.$refs.businessProcessesGrid) {
        this.$refs.businessProcessesGrid.refreshSortData();
        if (this.options.sorting === STATS_SORTING.Health) {
          this.$refs.businessProcessesGrid.sort('status name');
        } else {
          this.$refs.businessProcessesGrid.sort('order name');
        }
      }
    },

    onSetupClick() {
      this.$refs.setupDialog.open();
    },

    handleUpdateDashboard(options) {
      this.options = { ...options };
    },

    gridDragStartPredicate(item, event) {
      if (this.isGridLoading || this.options.sorting !== STATS_SORTING.Custom) {
        return false;
      }
      return Muuri.ItemDrag.defaultStartPredicate(item, event);
    }
  }
};
</script>

<style scoped>
.supervisor-information-center {
  padding: 6px 6px 64px 6px;
}

.grid {
  min-height: 100%;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  height: calc(100vh - 64px - 48px);
}

.item {
  display: block;
  position: absolute;
  margin: 6px;
  z-index: 1;
}

.item.muuri-item-dragging {
  z-index: 3;
}

.item:hover,
.item.muuri-item-releasing {
  z-index: 2;
}

.item.muuri-item-hidden {
  z-index: 0;
}

.item-content {
  position: relative;
  height: 100%;
  width: 100%;
}

.dashboard-setup-btn {
  position: fixed;
  right: 12px;
  bottom: 60px;
  z-index: 10;
}
</style>
<style>
.test-class {
  background-color: #000;
}
</style>
